import React, {useState} from "react";
import TextQuestion from "./TextQuestion";
import TextareaQuestion from "./TextareaQuestion";
import EmailQuestion from "./EmailQuestion";
import ButtonQuestion from "./ButtonQuestion";
import ButtonImageQuestion from "./ButtonImageQuestion";
import ButtonImageInnerQuestion from "./ButtonImageInnerQuestion";
import RadioQuestion from "./RadioQuestion";
import SelectQuestion from "./SelectQuestion";
import RadioImageQuestion from "./RadioImageQuestion";
import RangeQuestion from "./RangeQuestion";
import CheckboxQuestion from "./CheckboxQuestion";
import SlidersGroupQuestion from "./SlidersGroupQuestion";
import CheckboxGroupQuestion from "./CheckboxGroupQuestion";
import GroupTitle from "./GroupTitle";
import SeparatorTitle from "./SeparatorTitle";

const QuestionComponent = ({ questions = [], onChange, onBlur, answers }) => {
    const [openGroups, setOpenGroups] = useState({});

    // console.log('questions', questions);

    const toggleGroup = (groupId) => {
        setOpenGroups(prev => ({
            ...prev,
            [groupId]: !prev[groupId]
        }));
    };

    const isVisible = (question) => {
      if (!question.visible_if) return true;

      const triggerAnswer = answers[question.visible_if.sym_id];
      
      if (Array.isArray(triggerAnswer?.answer)) {
        // Caso seja um array de respostas (ex: checkboxes)
        return triggerAnswer.answer.includes(question.visible_if.value);
      }

      return triggerAnswer?.answer === question.visible_if.value;
    };

  const getQuestionValue = (question) => {
    const val = answers?.[question.sym_id];
    if (question.type === "check" && question.sym_for === 1) {
      return val?.items || [];
    }
    // Corrigir para checkboxes múltiplos (sym_for:2) salvos como objeto numerado
    if (question.type === "check" && question.sym_for === 2) {
      if (val && typeof val === 'object' && !Array.isArray(val) && val !== null) {
        // Se for objeto com chaves numéricas, retorna os valores como array
        return Object.values(val).filter(v => typeof v === 'number');
      }
      return Array.isArray(val?.answer) ? val.answer : [];
    }
    return val?.answer || "";
  };

    const renderQuestion = (question) => {
      const commonProps = {
        question,
        value: getQuestionValue(question),
        onChange: (val) => onChange(question.sym_id, val, question),
        onBlur: (val) => onBlur && onBlur(question.sym_id, val, question),
      };

      let content = null;

      switch (question.type) {
        case "text":
          content = <TextQuestion {...commonProps} />;
          break;
        case "textarea":
          content = <TextareaQuestion {...commonProps} />;
          break;
        case "email":
          content = <EmailQuestion {...commonProps} />;
          break;
        case "button":
          content = <ButtonQuestion {...commonProps} />;
          break;
        case "buttonImage":
          content = <ButtonImageQuestion {...commonProps} />;
          break;
        case "buttonImageInner":
          content = <ButtonImageInnerQuestion {...commonProps} />;
          break;
        case "radio":
          content = <RadioQuestion {...commonProps} />;
          break;
        case "select":
          content = <SelectQuestion {...commonProps} />;
          break;
        case "radioImage":
          content = <RadioImageQuestion {...commonProps} />;
          break;
        case "check":
          content = <CheckboxQuestion {...commonProps} />;
          break;
        case "range":
          content = <RangeQuestion {...commonProps} />;
          break;
        case "slidersGroup":
          content = (
            <SlidersGroupQuestion
              question={{ label: question.label, description: question.description }}
              answers={answers}
              onChange={(sym_id, value, itemQuestion) =>
                onChange(sym_id, value, {
                  ...itemQuestion,
                  groupId: question.groupId,
                  sub_id: question.sub_id,
                  sym_for: itemQuestion.sym_for ?? question.sym_for,
                })
              }
              groupItems={question.groupItems}
            />
          );
          break;
        case "checkGroup":
          content = (
            <CheckboxGroupQuestion
              question={question}
              answers={answers}
              onChange={onChange}
            />
          );
          break;
        case "categoryTitle":
          content = <GroupTitle question={question} />;
          break;
        case "separatorTitle":
        default:
          content = null;
          break;
      }

      if (!content) return null;

      return (
        <div key={question.sym_id} data-question-sym-id={question.sym_id}>
          {content}
        </div>
      );
    };

    // Nova estrutura de agrupamento com ordenação correta
    const sortedQuestions = [...questions]
      .filter(q => !q.hidden)
      .sort((a, b) => (a.sym_order ?? 0) - (b.sym_order ?? 0));

    const groupMap = new Map();
    const soloElements = [];

    sortedQuestions.forEach(question => {
      if (question.type === "separatorTitle") {
        const groupId = question.sym_group ?? question.sym_id;
        groupMap.set(groupId, {
          groupId,
          title: question.label,
          sym_order: question.sym_order ?? 0,
          isOpen: openGroups[groupId] ?? false,
          questions: [],
          header: (
            <div
              key={`group-${groupId}`}
              className="SeparatorTitle d-flex justify-content-between align-items-center"
              onClick={() => toggleGroup(groupId)}
              style={{ cursor: "pointer" }}
            >
              <h3 className="m-0">{question.label}</h3>
              <span>{openGroups[groupId] ? "▲" : "▼"}</span>
            </div>
          ),
        });
      } else if (question.sym_group && groupMap.has(question.sym_group)) {
        groupMap.get(question.sym_group).questions.push(question);
      } else {
        soloElements.push({
          groupId: `solo-${question.sym_id}`,
          header: null,
          isOpen: true,
          questions: [question],
        });
      }
    });

    const groupedElements = [
      ...Array.from(groupMap.values()),
      ...soloElements,
    ].sort((a, b) => {
      const aOrder = a.questions?.[0]?.sym_order ?? 9999;
      const bOrder = b.questions?.[0]?.sym_order ?? 9999;
      return aOrder - bOrder;
    });

    return (
      groupedElements.map((group) => (
        <div key={`block-${group.groupId}`} className={`question-container${group.header ? ' mb-4' : ''}`}>
          {group.header}
          {group.isOpen && (
            group.header ? (
              <div className="perguntas-do-grupo p-3 p-lg-4">
                {group.questions.filter(isVisible).map((question) => {
                  return renderQuestion(question);
                })}
              </div>
            ) : (
              group.questions.filter(isVisible).map((question) => {
                return renderQuestion(question);
              })
            )
          )}
        </div>
      ))
    );
};

export default QuestionComponent;
